import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileSpreadsheet, SearchIcon, PrinterIcon } from 'lucide-react';
import { BillReceipt } from '../components/BillReceipt';
interface BilledEntry {
  billNo: string;
  gatePassNo: string;
  vehicleNo: string;
  amount: number;
  paymentMode: 'Cash' | 'QR' | 'Coupon';
  dateTime: string;
  status: 'Paid' | 'Pending';
}
const sampleBilledData: BilledEntry[] = [{
  billNo: 'INV-2026-1001',
  gatePassNo: 'GP-2026-0001',
  vehicleNo: 'MH-12-AB-1234',
  amount: 350,
  paymentMode: 'Cash',
  dateTime: '2026-04-15 09:30 AM',
  status: 'Paid'
}, {
  billNo: 'INV-2026-1002',
  gatePassNo: 'GP-2026-0002',
  vehicleNo: 'MH-14-CD-5678',
  amount: 150,
  paymentMode: 'QR',
  dateTime: '2026-04-15 10:15 AM',
  status: 'Paid'
}, {
  billNo: 'INV-2026-1003',
  gatePassNo: 'GP-2026-0003',
  vehicleNo: 'GJ-05-EF-9012',
  amount: 250,
  paymentMode: 'Coupon',
  dateTime: '2026-04-15 11:45 AM',
  status: 'Paid'
}, {
  billNo: 'INV-2026-1004',
  gatePassNo: 'GP-2026-0004',
  vehicleNo: 'DL-01-GH-3456',
  amount: 450,
  paymentMode: 'Cash',
  dateTime: '2026-04-15 12:00 PM',
  status: 'Paid'
}, {
  billNo: 'INV-2026-1005',
  gatePassNo: 'GP-2026-0005',
  vehicleNo: 'RJ-14-IJ-7890',
  amount: 350,
  paymentMode: 'QR',
  dateTime: '2026-04-15 01:30 PM',
  status: 'Pending'
}, {
  billNo: 'INV-2026-1006',
  gatePassNo: 'GP-2026-0006',
  vehicleNo: 'KA-01-KL-2345',
  amount: 150,
  paymentMode: 'Cash',
  dateTime: '2026-04-15 02:00 PM',
  status: 'Paid'
}, {
  billNo: 'INV-2026-1007',
  gatePassNo: 'GP-2026-0007',
  vehicleNo: 'TN-09-MN-6789',
  amount: 70,
  paymentMode: 'QR',
  dateTime: '2026-04-15 02:20 PM',
  status: 'Paid'
}, {
  billNo: 'INV-2026-1008',
  gatePassNo: 'GP-2026-0008',
  vehicleNo: 'UP-32-OP-0123',
  amount: 350,
  paymentMode: 'Coupon',
  dateTime: '2026-04-15 03:45 PM',
  status: 'Pending'
}];
export function BilledDetailsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [reprintEntry, setReprintEntry] = useState<BilledEntry | null>(null);
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return sampleBilledData;
    const q = searchQuery.toLowerCase();
    return sampleBilledData.filter(entry => entry.billNo.toLowerCase().includes(q) || entry.gatePassNo.toLowerCase().includes(q) || entry.vehicleNo.toLowerCase().includes(q) || entry.status.toLowerCase().includes(q) || entry.paymentMode.toLowerCase().includes(q));
  }, [searchQuery]);
  return <div className="w-full max-w-6xl mx-auto" data-id="element-222">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-5 xs:p-6 sm:p-8" data-id="element-223">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-224">
          <div className="flex items-center gap-3" data-id="element-225">
            <div className="bg-brand-teal/10 p-2 rounded-xl border border-brand-teal/20" data-id="element-226">
              <FileSpreadsheet className="w-6 h-6 text-brand-teal" data-id="element-227" />
            </div>
            <div data-id="element-228">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight" data-id="element-229">
                Billed Details
              </h2>
              <p className="text-sm text-slate-500" data-id="element-230">
                {filteredData.length} invoice
                {filteredData.length !== 1 ? 's' : ''} found
              </p>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-5" data-id="element-231">
          <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400 pointer-events-none" data-id="element-232" />
          <input type="text" placeholder="Search by bill no, gate pass, vehicle number, payment mode, or status..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal transition-all" data-id="element-233" />
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-slate-200" data-id="element-234">
          <table className="w-full text-sm" data-id="element-235">
            <thead data-id="element-236">
              <tr className="bg-slate-50 border-b border-slate-200" data-id="element-237">
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap" data-id="element-238">
                  Bill No.
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap" data-id="element-239">
                  Gate Pass No.
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap" data-id="element-240">
                  Vehicle No.
                </th>
                <th className="text-right px-4 py-3 font-semibold text-slate-600 whitespace-nowrap" data-id="element-241">
                  Total Amount
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap" data-id="element-242">
                  Payment Mode
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap" data-id="element-243">
                  Billing Date & Time
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap" data-id="element-244">
                  Status
                </th>
                <th className="text-center px-4 py-3 font-semibold text-slate-600 whitespace-nowrap" data-id="element-245">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-246">
              {filteredData.length === 0 ? <tr data-id="element-247">
                  <td colSpan={8} className="text-center py-12 text-slate-400" data-id="element-248">
                    No billing records found matching your search.
                  </td>
                </tr> : filteredData.map((entry, index) => {
              return <motion.tr key={entry.billNo} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: index * 0.03
              }} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60 transition-colors" data-id="element-249">
                      <td className="px-4 py-3.5 font-mono text-xs font-semibold text-slate-800 whitespace-nowrap" data-id="element-250">
                        {entry.billNo}
                      </td>
                      <td className="px-4 py-3.5 font-mono text-xs font-medium text-brand-teal whitespace-nowrap" data-id="element-251">
                        {entry.gatePassNo}
                      </td>
                      <td className="px-4 py-3.5 font-mono text-xs font-semibold text-slate-600 whitespace-nowrap" data-id="element-252">
                        {entry.vehicleNo}
                      </td>
                      <td className="px-4 py-3.5 text-right font-medium text-slate-800 whitespace-nowrap" data-id="element-253">
                        ₹{entry.amount.toFixed(2)}
                      </td>
                      <td className="px-4 py-3.5 text-slate-600 whitespace-nowrap" data-id="element-254">
                        {entry.paymentMode}
                      </td>
                      <td className="px-4 py-3.5 text-slate-500 whitespace-nowrap text-xs" data-id="element-255">
                        {entry.dateTime}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-256">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${entry.status === 'Paid' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`} data-id="element-257">
                          {entry.status}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-258">
                        <div className="flex items-center justify-center" data-id="element-259">
                          <button onClick={() => setReprintEntry(entry)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-teal/10 text-brand-teal border border-brand-teal/20 hover:bg-brand-teal/20 transition-colors" title="Reprint Invoice" data-id="element-260">
                            <PrinterIcon className="w-3.5 h-3.5" data-id="element-261" />
                            Reprint
                          </button>
                        </div>
                      </td>
                    </motion.tr>;
            })}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Reprint Modal */}
      <AnimatePresence data-id="element-262">
        {reprintEntry && <BillReceipt billNo={reprintEntry.billNo} gatePassNo={reprintEntry.gatePassNo} vehicleNo={reprintEntry.vehicleNo} amount={reprintEntry.amount} paymentMode={reprintEntry.paymentMode} dateTime={reprintEntry.dateTime} onClose={() => setReprintEntry(null)} data-id="element-263" />}
      </AnimatePresence>
    </div>;
}